import { useMutation, useQueryClient } from 'react-query';
import type { RetryInvoiceDataDto } from '@subscriptions/application/dtos';
import { getInvoiceDataUseCases } from '@subscriptions/composition';

/** Variables accepted by the retry invoice mutation */
export type RetryInvoiceDataVariables = {
  paymentUuid: string;
  /** Narrows the re-issuing to the failed CFDIs, leaving the payment's valid ones untouched. */
  onlyFailed?: boolean;
  /** Reaches a fiscal record deleted after the payment was invoiced. */
  includeDeleted?: boolean;
};

/**
 * Hook holding the request that runs only in the fiscal data `retry` flow.
 *
 * `reinvoice-sync` cancels the live CFDIs, drops their records and stamps the new ones in a
 * single synchronous request, so resolving here means the payment was re-invoiced. It resolves
 * only when every CFDI came back ISSUED: the use case turns a rejected one into a rejection,
 * since the endpoint answers 200 either way.
 *
 * The form awaits this after the fiscal record is persisted and before `onSuccess`, so a
 * rejection leaves the sheet open with the save already committed.
 *
 * Re-invoicing changes the invoicing state of the payment, so the payment history is invalidated
 * on success and any open list refetches instead of showing the stale row.
 *
 * @returns Mutation result for retrying the invoicing of a payment
 *
 * @example
 * const { mutateAsync: retryInvoiceData, isLoading } = useRetryInvoiceActions();
 * await retryInvoiceData({ paymentUuid });
 */
export const useRetryInvoiceActions = () => {
  const queryClient = useQueryClient();
  const { retryInvoiceData } = getInvoiceDataUseCases();

  return useMutation<RetryInvoiceDataDto, Error, RetryInvoiceDataVariables>(
    ({ paymentUuid, onlyFailed, includeDeleted }) => retryInvoiceData.execute(paymentUuid, onlyFailed, includeDeleted),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['subscriptions', 'payment-history']);
      },
    },
  );
};
